import React from 'react';
import {
  Box,
  Paper,
} from '@mui/material';
import { borderRadius } from '../../../theme/theme';
import MainContentArea from './components/MainContentArea';
import SelectionTree from './components/SelectionTree';
import WorkspaceCollapsibleRail from '../../../shared/ui/workflow/WorkspaceCollapsibleRail';
import type { MappingScopeSummary } from '../lib/mappingWorkspaceModel';
import type { ObjectType } from '../../../shared/types';
import type {
  MappingSelectedNode,
  MappingWorkspaceNavigatorSummary,
  MappingWorkspaceSectionSummaries,
} from '../lib/mappingWorkspacePageModel';
import type {
  ScopeEditorPersistenceState,
  ScopeEditorPersistenceStateUpdater,
} from '../lib/scopeEditorPersistence';
import type { MappingWorkspaceFocusRefs } from '../hooks/useMappingWorkspaceFocusRefs';

interface MappingWorkspaceCanvasProps {
  isMobile: boolean;
  mobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  onCloseMobileMenu: () => void;
  focusRefs: MappingWorkspaceFocusRefs;
  objectTypes: ObjectType[];
  selectedNode: MappingSelectedNode;
  onNodeSelect: (type: string, name?: string) => void;
  onAddObjectType: (objectTypeName: string) => void;
  onDeleteObjectType: (objectTypeName: string) => void;
  isTreeCollapsed: boolean;
  onToggleTreeCollapsed: () => void;
  scopeSummaries: Record<string, MappingScopeSummary>;
  sectionSummaries: MappingWorkspaceSectionSummaries;
  workspaceNavigatorSummary: MappingWorkspaceNavigatorSummary;
  highlightedAttributeNames: string[];
  processConditionsEditorState: ScopeEditorPersistenceState;
  onProcessConditionsEditorStateChange: (nextState: ScopeEditorPersistenceStateUpdater) => void;
  projectPropertiesEditorState: ScopeEditorPersistenceState;
  onProjectPropertiesEditorStateChange: (nextState: ScopeEditorPersistenceStateUpdater) => void;
}

const MappingWorkspaceCanvas: React.FC<MappingWorkspaceCanvasProps> = ({
  isMobile,
  mobileMenuOpen,
  onOpenMobileMenu,
  onCloseMobileMenu,
  focusRefs,
  objectTypes,
  selectedNode,
  onNodeSelect,
  onAddObjectType,
  onDeleteObjectType,
  isTreeCollapsed,
  onToggleTreeCollapsed,
  scopeSummaries,
  sectionSummaries,
  workspaceNavigatorSummary,
  highlightedAttributeNames,
  processConditionsEditorState,
  onProcessConditionsEditorStateChange,
  projectPropertiesEditorState,
  onProjectPropertiesEditorStateChange,
}) => (
  <Box
    ref={focusRefs.workspaceCanvas}
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minHeight: 0,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        borderRadius: `${borderRadius.none}px`,
        border: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minHeight: 0,
          position: 'relative',
          gap: 0,
        }}
      >
        <WorkspaceCollapsibleRail
          collapsed={isTreeCollapsed}
          onToggleCollapsed={onToggleTreeCollapsed}
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          onCloseMobileMenu={onCloseMobileMenu}
          tourRefs={{ railRef: focusRefs.navigation }}
        >
          <SelectionTree
            objectTypes={objectTypes}
            selectedNode={selectedNode}
            onNodeSelect={onNodeSelect}
            onAddObjectType={onAddObjectType}
            onDeleteObjectType={onDeleteObjectType}
            collapsed={isMobile ? false : isTreeCollapsed}
            generalItemRef={focusRefs.generalNav}
            selectedObjectTypeItemRef={focusRefs.selectedObjectTypeNav}
            processConditionsItemRef={focusRefs.processConditionsNav}
            projectPropertiesItemRef={focusRefs.projectParametersNav}
            statusBuilderNavRef={focusRefs.statusBuilderNav}
            statusCategoryFirstNavRef={focusRefs.statusCategoryNav}
            scopeSummaries={scopeSummaries}
            sectionSummaries={sectionSummaries}
            workspaceSummary={workspaceNavigatorSummary}
            onMobileMenuToggle={isMobile ? onCloseMobileMenu : undefined}
            mobileMenuOpen={mobileMenuOpen}
            hideMobileHeader={isMobile}
          />
        </WorkspaceCollapsibleRail>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
            <MainContentArea
              selectedNode={selectedNode}
              highlightedAttributeNames={highlightedAttributeNames}
              mappingCanvasRef={focusRefs.mappingCanvas}
              tableControlsRef={focusRefs.tableControls}
              pcfColumnRef={focusRefs.pcfColumn}
              revitColumnRef={focusRefs.revitColumn}
              typeColumnRef={focusRefs.typeColumn}
              formulaColumnRef={focusRefs.formulaColumn}
              rowActionsRef={focusRefs.rowActions}
              generalSectionRef={focusRefs.generalSection}
              processConditionsRef={focusRefs.processConditions}
              projectParametersRef={focusRefs.projectParameters}
              advancedSectionRef={focusRefs.advancedSection}
              statusBuilderSectionRef={focusRefs.statusBuilderSection}
              statusCategorySectionRef={focusRefs.statusCategorySection}
              processConditionsEditorState={processConditionsEditorState}
              onProcessConditionsEditorStateChange={onProcessConditionsEditorStateChange}
              projectPropertiesEditorState={projectPropertiesEditorState}
              onProjectPropertiesEditorStateChange={onProjectPropertiesEditorStateChange}
              onMobileMenuToggle={isMobile ? onOpenMobileMenu : undefined}
              mobileMenuButtonRef={focusRefs.mobileMenuButton}
              mobileMenuOpen={mobileMenuOpen}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );

export default MappingWorkspaceCanvas;
